import { Area, AreaLayer, SearchArea } from '@teko/location';
import { useServices, useTenantConfig } from 'contexts';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { LocationLimit } from './useLocation';

const filterLocations = (searchAreas: SearchArea[], limits: string[]) => {
  const areas = searchAreas.map(
    ({ id, code, title }) => ({ id, code, title } as Area),
  );
  return limits && limits.length > 0
    ? areas.filter((area) =>
        limits.some((allowDis) => {
          const re = new RegExp(`^${allowDis}$`);
          return re.test(area.code);
        }),
      )
    : areas;
};

export const useLocationsV2 = (
  defaultProvinceCode?: string,
  defaultWardCode?: string,
  limit: LocationLimit = {},
) => {
  const { locationServiceV3 } = useServices();
  const { epicOmni1589 } = useTenantConfig();

  const [province, setProvince] = useState<Area>();
  const [ward, setWard] = useState<Area>();

  const { data: provinces = [] } = useSWR(
    epicOmni1589 ? ['provinces'] : null,
    async () => {
      const response = await locationServiceV3.getAreas({
        areaLayer: AreaLayer.province,
      });
      return filterLocations(response || [], limit.provinces || []);
    },
    { revalidateOnFocus: false, fallbackData: [], shouldRetryOnError: false },
  );

  const { data: wards = [], mutate: refetchWards } = useSWR(
    epicOmni1589 && province?.id ? ['wards', province.id] : null,
    async () => {
      const response = await locationServiceV3.getAreas({
        areaLayer: AreaLayer.ward,
        parentAreaId: province?.id,
      });
      return filterLocations(response || [], limit.wards || []);
    },
    { revalidateOnFocus: false, fallbackData: [], shouldRetryOnError: false },
  );

  useEffect(() => {
    if (defaultProvinceCode && provinces.length > 0) {
      const defaultProv = provinces.find((p) => p.code === defaultProvinceCode);
      if (defaultProv) setProvince(defaultProv);
    }
  }, [provinces, defaultProvinceCode]);

  useEffect(() => {
    if (defaultWardCode && wards.length > 0) {
      const defaultW = wards.find((w) => w.code === defaultWardCode);
      if (defaultW) setWard(defaultW);
    }
  }, [wards, defaultWardCode]);

  const onProvinceChanged = (code: string) => {
    const selected = provinces.find((p) => p.code === code);
    if (selected) {
      setProvince(selected);
      setWard(undefined);
    } else {
      setProvince(undefined);
      setWard(undefined);
    }
  };

  const onWardChanged = (code: string) => {
    const selected = wards.find((w) => w.code === code);
    if (selected) {
      setWard(selected);
    } else {
      setWard(undefined);
    }
  };

  const isValidLocation = () => {
    return !!province?.code && (!!ward?.code || wards.length === 0);
  };

  const setLocation = async ({
    provinceId,
    wardId,
  }: {
    provinceId: string;
    wardId: string;
  }) => {
    const province = provinces.find((p) => p.id === Number(provinceId));
    if (!province) return;
    await refetchWards();
    const ward = wards.find((w) => w.id === Number(wardId));
    setProvince(province);
    setWard(ward);
  };

  return {
    provinces,
    wards,
    province,
    ward,
    setProvince,
    setWard,
    onProvinceChanged,
    onWardChanged,
    isValidLocation,
    setLocation,
  };
};
